import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import { TeacherRadar } from '@/components/radar/TeacherRadar'
import { CalendarCard, CalendarConnectModal } from '@/components/calendar'
import { useTeacherRadar } from '@/hooks/useTeacherRadar'
import { useDirectorEnabled } from '@/hooks/useDirectorEnabled'
import { useCalendarFeed } from '@/hooks/useCalendarFeed'
import { TestRollbackButton } from '@/components/email/TestRollbackButton'
import { teacherApi } from '@/services/api/teacher'
import type { RadarBubble } from '@/services/api/teacher'
import { getTargetRoute, getRequestIdFromBubble } from '@/utils/radarNavigation'

declare global {
  interface Window {
    gtag?: (command: string, eventName: string, params?: Record<string, unknown>) => void
  }
}

interface TeacherDashboardConfig {
  bookings_panel_enabled: boolean
  bookings_limit: number
  calendar_connect_enabled: boolean
}

export function TeacherDashboardPage() {
  const navigate = useNavigate()
  const directorEnabled = useDirectorEnabled()
  const {
    radarData,
    radarConfig,
    timeRangeDays,
    isLoadingRadar,
    isLoadingConfig,
    error,
    message,
    setMessage,
    outgoingBubbles,
    incomingBubbles,
    fetchRadarBubbles,
    handleTimeRangeChange,
    filterState,
    setFilter,
    resetFilter,
    isFilterLocked,
    filterDefaultState,
  } = useTeacherRadar()

  // dashboard-v3: Panel-Sichtbarkeit + Kalender-Verbinden-Freigabe
  const [dashboardConfig, setDashboardConfig] = useState<TeacherDashboardConfig | null>(null)
  const [showCalendarModal, setShowCalendarModal] = useState(false)
  const { status: calendarStatus, fetchStatus: fetchCalendarStatus } = useCalendarFeed()

  useEffect(() => {
    teacherApi.getDashboardConfig()
      .then(setDashboardConfig)
      .catch(() => setDashboardConfig({ bookings_panel_enabled: true, bookings_limit: 3, calendar_connect_enabled: true }))
  }, [])

  useEffect(() => {
    fetchCalendarStatus()
  }, [fetchCalendarStatus])

  // Bis zum Eintreffen der Konfiguration Defaults annehmen (kein Flackern)
  const showBookingsPanel =
    dashboardConfig?.bookings_panel_enabled !== false &&
    dashboardConfig?.bookings_limit !== 0
  const showCalendarConnect = dashboardConfig?.calendar_connect_enabled !== false
  const isCalendarConnected = calendarStatus?.enabled && calendarStatus?.feed_url

  const [isNavigating, setIsNavigating] = useState(false)
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const handleBubbleClick = useCallback((bubble: RadarBubble) => {
    // Vorheriges Timeout canceln (Race Condition vermeiden)
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current)
    }

    if (window.gtag) {
      window.gtag('event', 'radar_bubble_click', {
        event_category: 'Radar',
        event_label: bubble.bubble_type || 'unknown',
        request_id: bubble.request_id,
        bubble_id: bubble.id,
      })
    }

    const targetRoute = getTargetRoute(bubble)
    const requestId = getRequestIdFromBubble(bubble)

    setIsNavigating(true)
    navigationTimeoutRef.current = setTimeout(() => {
      navigate(`${targetRoute}?highlight=${requestId}`)
      setIsNavigating(false)
    }, 150)
  }, [navigate])

  // Cleanup bei Unmount
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div className="p-6">
      <TestRollbackButton />
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        {showCalendarConnect && (
          <button
            onClick={() => setShowCalendarModal(true)}
            className={
              isCalendarConnected
                ? 'inline-flex items-center justify-center px-3 py-1.5 rounded-lg font-medium text-sm bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors'
                : 'inline-flex items-center justify-center px-3 py-1.5 rounded-lg font-medium text-sm bg-gray-200 text-gray-900 hover:bg-gray-300 transition-colors'
            }
          >
            Kalender verbinden
          </button>
        )}
      </div>

      {message && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {message}
          <button onClick={() => setMessage(null)} className="ml-2 text-sm underline">Schliessen</button>
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
          <button onClick={() => setMessage(null)} className="ml-2 text-sm underline">Schliessen</button>
        </div>
      )}

      {showBookingsPanel && <CalendarCard className="mb-4" />}

      <TeacherRadar
        bubbles={radarData?.bubbles || []}
        outgoingBubbles={outgoingBubbles}
        incomingBubbles={incomingBubbles}
        zones={radarData?.zones}
        rays={radarData?.rays || []}
        timeRange={radarData?.time_range}
        config={radarConfig}
        isLoading={isLoadingRadar}
        isConfigLoading={isLoadingConfig}
        onBubbleClick={handleBubbleClick}
        onTimeRangeChange={handleTimeRangeChange}
        onRefresh={() => fetchRadarBubbles(timeRangeDays)}
        filterState={filterState}
        onFilterChange={setFilter}
        onResetFilter={resetFilter}
        isFilterLocked={isFilterLocked}
        filterDefaultState={filterDefaultState}
        directorEnabled={directorEnabled}
      />

      {isNavigating && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg flex items-center gap-2">
            <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
            <span className="text-gray-700 dark:text-gray-300 text-sm">Navigiere...</span>
          </div>
        </div>
      )}

      <CalendarConnectModal
        isOpen={showCalendarModal}
        onClose={() => {
          setShowCalendarModal(false)
          // Status nachziehen — Modal-Aktionen (subscribe/reset/toggle) laufen
          // in der Hook-Instanz des Modals; ohne Refetch würde der Button-
          // Zustand (verbunden/nicht verbunden) veralten.
          fetchCalendarStatus()
        }}
        feedUrl={calendarStatus?.feed_url}
        qrUrl={calendarStatus?.qr_url}
        isEnabled={calendarStatus?.enabled}
      />
    </div>
  )
}
